import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Mail, Phone, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';

// Personalization — what the published public page discloses about staff.
//
// Sits apart from Design Studio on purpose: that panel decides how the page
// looks and every choice there is reversible cosmetics, whereas these two
// switches decide whether real people's contact details are published to
// anyone with the URL. Grouping them would invite treating a disclosure
// decision as a styling one.
//
// Academy-wide, not per staff member — one switch for everyone. A directory
// where some rows carry a phone number and others do not tells a visitor
// something about the individuals, and this is the control the academy asked
// for.

// The switches, described in the terms the person setting them thinks in.
const FLAGS = [
  {
    key: 'publishStaffEmail',
    icon: Mail,
    label: 'Publish staff email addresses',
    help: 'Visitors see each staff member’s email in the Contact column and can email them directly.',
  },
  {
    key: 'publishStaffPhone',
    icon: Phone,
    label: 'Publish staff phone numbers',
    help: 'Visitors see each staff member’s phone number and can call them directly.',
  },
];

// ⚠️ ITS OWN useTheme(). This sits at module scope, outside the component that
// reads the theme, so the switch cannot borrow `theme` from a closure — it has
// to ask for it. Without this the file threw "theme is not defined" the moment
// the toggle's on-colour stopped being a hardcoded blue.
const Toggle = ({ checked, onChange, disabled, label }) => {
  const { theme } = useTheme();

  return (
  <label className={`relative inline-flex items-center shrink-0 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      role="switch"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
      className="sr-only peer"
    />
    {/* Same switch the rest of the app uses (see CreateProgramModal) — kept
        identical rather than restyled, so a control that means the same thing
        looks the same everywhere. */}
    <div className={`w-11 h-6 bg-gray-300 ${theme.toggleOn} rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5`} />
  </label>
  );
};

const PersonalizationSettings = ({ academyId, onGoToPublish }) => {
  const { theme } = useTheme();

  const [config, setConfig] = useState(null);   // what is saved
  const [draft, setDraft] = useState(null);     // what is on screen
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Set once a save lands while a published page exists: the live page is a
  // static file and still carries the previous choice until the next publish.
  const [staleSincePublish, setStaleSincePublish] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await academyAPI.getPersonalization(academyId);
      setConfig(data);
      setDraft(data);
    } catch (err) {
      console.error('[Personalization] load failed', err);
      toast.error('Could not load personalization settings');
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => { load(); }, [load]);

  const dirty = config && draft && FLAGS.some(f => config[f.key] !== draft[f.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await academyAPI.updatePersonalization(academyId, {
        publishStaffEmail: draft.publishStaffEmail,
        publishStaffPhone: draft.publishStaffPhone,
      });
      const { republishRequired, ...saved } = result;
      setConfig(saved);
      setDraft(saved);
      setStaleSincePublish(Boolean(republishRequired));
      toast.success('Personalization saved');
    } catch (err) {
      console.error('[Personalization] save failed', err);
      toast.error(err?.message || 'Could not save personalization settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 justify-center py-16 ${theme.textMuted}`}>
        <Loader2 size={18} className="animate-spin" />
        <span className="text-sm">Loading settings…</span>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className={`text-center py-16 ${theme.textMuted}`}>
        <p className="text-sm">Settings unavailable.</p>
        <button onClick={load} className={`mt-3 px-4 py-2 rounded-lg text-sm ${theme.buttonSecondary}`}>Retry</button>
      </div>
    );
  }

  const bothOff = !draft.publishStaffEmail && !draft.publishStaffPhone;

  return (
    <div>
      <div className="mb-6">
        <h3 className={`text-2xl font-bold ${theme.text}`}>Personalization</h3>
        <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
          Choose what your published public page shows about your staff.
        </p>
      </div>

      {/* Why this exists, stated once so the choice is made knowingly. */}
      <div className={`flex gap-3 p-4 rounded-xl border ${theme.border} ${theme.backgroundSecondary} mb-5`}>
        <ShieldCheck size={18} className={`${theme.textMuted} shrink-0 mt-0.5`} />
        <p className={`text-sm ${theme.textSecondary}`}>
          Your published page is public — anyone with the link can read it, without signing in.
          Anything switched on below is visible to them and to search engines. These settings apply
          to every staff member in this academy.
        </p>
      </div>

      <div className={`${theme.card} border rounded-xl divide-y ${theme.border} mb-5`}>
        {FLAGS.map(({ key, icon: Icon, label, help }) => (
          <div key={key} className="flex items-start justify-between gap-4 p-4">
            <div className="flex gap-3 min-w-0">
              <Icon size={18} className={`${theme.textMuted} shrink-0 mt-0.5`} />
              <div className="min-w-0">
                <p className={`text-sm font-medium ${theme.text}`}>{label}</p>
                <p className={`text-xs ${theme.textMuted} mt-0.5`}>{help}</p>
              </div>
            </div>
            <Toggle
              label={label}
              checked={draft[key]}
              disabled={saving}
              onChange={v => setDraft(d => ({ ...d, [key]: v }))}
            />
          </div>
        ))}
      </div>

      {/* What the visitor ends up seeing, in the visitor's terms. The Contact
          column is dropped entirely when neither is on, rather than published
          as a row of blanks — so say that, since it is not obvious from two
          switches. */}
      <div className={`p-4 rounded-xl border ${theme.border} mb-5`}>
        <p className={`text-xs font-semibold uppercase tracking-wider ${theme.textMuted} mb-2`}>
          On your public page
        </p>
        <p className={`text-sm ${theme.textSecondary}`}>
          {bothOff
            ? 'The staff directory lists names and roles only — the Contact column is not published at all.'
            : draft.publishStaffEmail && draft.publishStaffPhone
              ? 'The staff directory shows each member’s name, role, email and phone number.'
              : draft.publishStaffEmail
                ? 'The staff directory shows each member’s name, role and email address. Phone numbers are withheld.'
                : 'The staff directory shows each member’s name, role and phone number. Email addresses are withheld.'}
        </p>
        <p className={`text-xs ${theme.textMuted} mt-2`}>
          Visitors can always send a message through the app — that asks them to sign in first, so it
          never exposes contact details.
        </p>
      </div>

      {/* Saving records the choice; it does not rewrite the page already sitting
          in front of visitors. Saying so is the difference between the owner
          believing details are down and them actually being down. */}
      {staleSincePublish && !dirty && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 mb-5">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className={`text-sm font-medium ${theme.text}`}>Your live page hasn’t changed yet</p>
            <p className={`text-xs ${theme.textSecondary} mt-0.5`}>
              The published page still shows the previous setting. Republish it to apply this change.
            </p>
            {onGoToPublish && (
              <button
                onClick={onGoToPublish}
                className="mt-2 text-xs font-medium text-amber-500 hover:underline"
              >
                Go to Publish to Web →
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`px-5 py-2.5 rounded-lg font-medium text-sm ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {dirty && !saving && (
          <button
            onClick={() => setDraft(config)}
            className={`px-4 py-2.5 rounded-lg text-sm border ${theme.border} ${theme.text} ${theme.cardHover}`}
          >
            Discard
          </button>
        )}
        {dirty && <span className={`text-xs ${theme.textMuted}`}>Unsaved changes</span>}
      </div>
    </div>
  );
};

export default PersonalizationSettings;
